import { forwardRef, ReactElement, useImperativeHandle } from "react";
import BsTable from "react-bootstrap/Table";
import {
  Column,
  ColumnInstance,
  Row,
  TableRowProps,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import useResponsive from "../../../hooks/useResponsive";
import { PropsWithClassName } from "../../../models/props-with-class-name";
import styles from "./Table.module.css";
import TableCheckBox from "./TableCheckBox/TableCheckBox";
import InfiniteScrollWrapper from "../InfiniteScrollWrapper/InfiniteScrollWrapper";
import { InfiniteScrollConfigType } from "../../../utils/infiniteScrollUtil";
import { Constants } from "../../../constants";

export type TableRef = {
  curPage: number;
  nextDisabled: boolean;
  backDisabled: boolean;
  globalFilter: any;
  selectedCheckedRows: any[];
  setGlobalFilter: (filterValue: any) => void;
  onNextPage: () => void;
  onPreviousPage: () => void;
};

type TableProps<T extends { id: string }> = PropsWithClassName<{
  tableData: T[];
  tableColumns: Column<T>[];
  itemsPerPage?: number;
  currentPage?: number;
  onRowSelect?: (item: T) => void;
  selectedRowId?: string;
  totalPages?: number;
  serverSide?: boolean;
  sortable?: boolean;
  checkboxSelect?: boolean;
  checkboxSelectDisabled?: boolean;
  customRowProps?: (row?: Row<T>) => Omit<TableRowProps, "key">;
  scrollConfig?: InfiniteScrollConfigType<T>;
}>;

const TableInner = <T extends { id: string }>(
  {
    tableData,
    tableColumns,
    itemsPerPage,
    currentPage,
    onRowSelect,
    selectedRowId,
    className,
    totalPages,
    serverSide,
    sortable = false,
    checkboxSelect = false,
    checkboxSelectDisabled,
    customRowProps = () => ({}),
    scrollConfig,
  }: TableProps<T>,
  ref: React.ForwardedRef<TableRef>
) => {
  const tableInstance = useTable(
    {
      columns: tableColumns,
      data: tableData,
      initialState: {
        pageIndex: currentPage ?? 0,
        pageSize: itemsPerPage ?? Constants.TABLE_ITEMS_PER_PAGE,
      },
      manualPagination: serverSide,
      pageCount: 10000000,
      disableSortBy: !sortable,
      autoResetSelectedRows: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      if (!checkboxSelect) return;
      hooks.visibleColumns.push((columns: ColumnInstance<T>[]) => [
        // Add column for selection
        {
          id: "selection",
          Header: ({ getToggleAllRowsSelectedProps }) => {
            const checkboxProps = {
              ...getToggleAllRowsSelectedProps(),
              disabled: checkboxSelectDisabled,
            };
            return <TableCheckBox {...checkboxProps} />;
          },
          Cell: ({ row }: { row: Row<T> }) => {
            const checkboxProps = {
              ...row?.getToggleRowSelectedProps(),
              disabled: checkboxSelectDisabled,
            };
            return <TableCheckBox {...checkboxProps} />;
          },
        },
        ...columns,
      ]);
    }
  );

  const {
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setGlobalFilter,
    // selectedFlatRows,
    toggleRowSelected,
    state: { pageIndex, globalFilter },
  } = tableInstance;

  const { selectedFlatRows = undefined } = checkboxSelect ? tableInstance : {};

  useImperativeHandle(
    ref,
    (): TableRef => ({
      curPage: pageIndex,
      nextDisabled: !canNextPage,
      backDisabled: !canPreviousPage,
      selectedCheckedRows:
        selectedFlatRows?.map((row) => {
          return row.original;
        }) ?? [],
      onNextPage: () => {
        nextPage();
      },
      onPreviousPage: () => {
        if (!canPreviousPage) return;
        previousPage();
      },
      globalFilter: globalFilter,
      setGlobalFilter: setGlobalFilter,
    }),
    [
      pageIndex,
      canNextPage,
      canPreviousPage,
      globalFilter,
      nextPage,
      previousPage,
      setGlobalFilter,
      selectedFlatRows,
    ]
  );

  const { isMobile } = useResponsive();

  const onRowClick = (row: Row<T>) => {
    const { original } = row;

    if (selectedRowId === original.id) return;

    if (onRowSelect) onRowSelect(original);

    if (checkboxSelect && toggleRowSelected && !checkboxSelectDisabled) {
      toggleRowSelected(row.id);
    }
  };

  const infiniteScrollEnabled = !!scrollConfig;

  return (
    <div
      id={
        infiniteScrollEnabled ? scrollConfig?.scrollableTarget?.toString() : ""
      }
      className={styles.wrapper}
    >
      <InfiniteScrollWrapper
        enableInfiniteScroll={infiniteScrollEnabled}
        scrollConfig={scrollConfig}
      >
        <BsTable className={`${className} mb-0 position-absolute`} size="sm">
          <thead className="app-shadow font-md position-sticky top-0 left-0">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {!isMobile && (
                  <th
                    className={`${styles.tableHead} ${styles.emptyCell}`}
                  ></th>
                )}
                {headerGroup.headers.map((column) => (
                  <th
                    className={styles.tableHead}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps([
                    customRowProps(row),
                    {
                      // @ts-ignore
                      onClick: (event) => {
                        event.preventDefault();
                        onRowClick(row);
                      },
                      className: `${styles.row} ${
                        selectedRowId === row.original.id
                          ? styles.selectedRow
                          : ""
                      }`,
                    },
                  ])}
                >
                  {!isMobile && <td className={styles.emptyCell}></td>}

                  {row.cells.map((cell) => {
                    return (
                      <td
                        {...cell.getCellProps({
                          // @ts-ignore
                          className: `${styles.cell} ${cell.column.className ??
                            ""}`,
                        })}
                        width={cell.column?.width}
                      >
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          {infiniteScrollEnabled && (
            <tfoot>
              <tr>{scrollConfig?.hasMore ? scrollConfig?.customLoader : ""}</tr>
            </tfoot>
          )}
        </BsTable>
      </InfiniteScrollWrapper>
    </div>
  );
};

const Table = forwardRef(TableInner) as <T extends { id: string }>(
  p: TableProps<T> & { ref?: React.Ref<TableRef> }
) => ReactElement;

export default Table;
